import { useMemo } from 'react';

import {
  AppBar,
  Dialog,
  IconButton,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { CloudDownloadIcon, XIcon } from 'lucide-react';

import { SlideUpTransition } from '@modules/_shared/components/SlideDialog';
import { useLocale } from '@shared/hooks/locale';

type FileViewerDialogProps = {
  url?: string;
  name: string;
  open: boolean;
  onClose: () => void;
};

const supportObject = !/iPad|iPhone|iPod/.test(navigator.userAgent); // do not show embed viewer on iOS

export const FileViewerDialog: React.FC<FileViewerDialogProps> = ({
  url,
  name,
  open,
  onClose,
}) => {
  const { t } = useLocale();
  const iframe = useMemo(() => {
    const encodedUri = url == null ? null : encodeURIComponent(url);

    return encodedUri == null ? null : (
      <iframe
        title={name}
        css={{ flexGrow: 1 }}
        src={`https://docs.google.com/viewer?url=${encodedUri}&embedded=true`}
      />
    );
  }, [url, name]);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      slots={{
        transition: SlideUpTransition,
      }}
    >
      <AppBar
        position="static"
        sx={{ zIndex: theme => theme.zIndex.drawer + 2 }}
        elevation={1}
        color="white"
      >
        <Toolbar>
          <IconButton color="inherit" onClick={onClose} aria-label="close">
            <XIcon size={20} />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }} noWrap>
            {name}
          </Typography>
          <Tooltip title={t('Download as {{name}}', { name })}>
            <IconButton
              color="inherit"
              component="a"
              href={url ?? ''}
              disabled={url == null}
              download={true}
            >
              <CloudDownloadIcon size={20} />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      {supportObject ? (
        <object
          data={url}
          type="application/pdf"
          css={{ flex: 1, display: 'flex' }}
        >
          {iframe}
        </object>
      ) : (
        iframe
      )}
    </Dialog>
  );
};
